import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { config } from '@/global-config';
import { getDeviceInput } from '@/shared/utils/device';

// CSRF: api/ sets a non-httpOnly `csrf-token` cookie on login (per scaffold-plan.md §7).
// We mirror it back as `X-CSRF-Token` on mutating requests (double-submit cookie pattern).
const CSRF_COOKIE_NAME = 'csrf-token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const USER_REFRESH_PATH = '/api/auth/refresh';
const GUEST_REFRESH_PATH = '/api/guest/auth/refresh';

// Which endpoint `triggerRefresh` rotates against. Guest sessions mint tokens
// via /api/guest/* (different token audience), so AuthProvider flips this to
// 'guest' on guest login / guest bootstrap and back to 'user' on logout.
let refreshMode: 'user' | 'guest' = 'user';

export function setRefreshMode(mode: 'user' | 'guest'): void {
  refreshMode = mode;
}

function getRefreshPath(): string {
  return refreshMode === 'guest' ? GUEST_REFRESH_PATH : USER_REFRESH_PATH;
}

function isRefreshPath(url: string | undefined): boolean {
  return url?.endsWith(USER_REFRESH_PATH) === true || url?.endsWith(GUEST_REFRESH_PATH) === true;
}

// Schedule the proactive refresh this many ms before the access token's
// `expiresAt`. One minute leaves room for network latency + clock skew without
// burning through the token's useful life.
const PROACTIVE_REFRESH_LEAD_MS = 60_000;

// In-memory access token + matching expiry. NEVER persisted to localStorage
// (protocol). The refresh token lives in an httpOnly cookie set by api/ — that
// cookie is what lets us restore the session after a page reload.
let accessToken: string | null = null;
let accessTokenExpiresAt: Date | null = null;
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;

export function setAccessToken(token: string | null, expiresAt?: Date | null): void {
  accessToken = token;
  accessTokenExpiresAt = expiresAt ?? null;
  scheduleProactiveRefresh();
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getAccessTokenExpiresAt(): Date | null {
  return accessTokenExpiresAt;
}

// Auth-failure handler — auth-context registers itself here so axios can clear user state
// when refresh fails (avoids circular import: auth-context imports from this file, not the reverse).
type AuthFailureHandler = () => void | Promise<void>;
let authFailureHandler: AuthFailureHandler | null = null;

export function setAuthFailureHandler(handler: AuthFailureHandler | null): void {
  authFailureHandler = handler;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match?.split('=')[1];
}

function attachAuthHeaders(req: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (req.method && MUTATING_METHODS.has(req.method.toLowerCase())) {
    const csrf = readCookie(CSRF_COOKIE_NAME);
    if (csrf) req.headers.set(CSRF_HEADER_NAME, csrf);
  }
  if (accessToken) req.headers.set('Authorization', `Bearer ${accessToken}`);
  return req;
}

// Plain axios for refresh — no interceptors, no recursion.
const bareClient = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
});

interface RefreshResponse {
  accessToken?: string;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
}

async function performRefresh(): Promise<void> {
  // api/'s refreshTokenSchema requires `device` in the body; the refresh-token
  // itself is read from the httpOnly cookie for web clients (no body needed).
  const res = await bareClient.post<RefreshResponse>(getRefreshPath(), {
    device: getDeviceInput(),
  });
  if (res.data.accessToken) {
    const expiry = res.data.accessTokenExpiresAt ? new Date(res.data.accessTokenExpiresAt) : null;
    setAccessToken(res.data.accessToken, expiry);
  }
}

// Single-flight: concurrent refreshes (proactive timer + reactive 401 retry +
// multiple parallel requests all 401-ing) share ONE in-flight promise.
let refreshPromise: Promise<void> | null = null;

/**
 * Attempt to refresh the access token using the httpOnly refresh cookie.
 *
 * - Shared single-flight across the whole app (proactive + reactive paths).
 * - On success: state is updated by `performRefresh` and the next proactive
 *   timer is scheduled.
 * - On failure: in-memory state is cleared and `authFailureHandler` (set by
 *   AuthProvider) fires so the UI can redirect to login.
 *
 * Re-throws so callers can react (the response interceptor uses this to decide
 * whether to retry the original request).
 */
export async function triggerRefresh(): Promise<void> {
  refreshPromise ??= performRefresh();
  try {
    await refreshPromise;
  } catch (err) {
    setAccessToken(null);
    await authFailureHandler?.();
    throw err;
  } finally {
    refreshPromise = null;
  }
}

function scheduleProactiveRefresh(): void {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  if (!accessToken || !accessTokenExpiresAt) return;

  const msUntilRefresh =
    accessTokenExpiresAt.getTime() - Date.now() - PROACTIVE_REFRESH_LEAD_MS;

  if (msUntilRefresh <= 0) {
    // Token is already in (or past) the lead window — refresh now. Swallow
    // any failure; `triggerRefresh` already notifies the failure handler.
    void triggerRefresh().catch(() => {});
    return;
  }

  proactiveTimer = setTimeout(() => {
    void triggerRefresh().catch(() => {});
  }, msUntilRefresh);
}

interface RetryFlag {
  _retry?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use(attachAuthHeaders);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & RetryFlag) | undefined;

    // Pass through anything that isn't a 401 we can recover from.
    if (
      error.response?.status !== 401 ||
      !original ||
      original._retry ||
      isRefreshPath(original.url)
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      await triggerRefresh();
      return await api.request(original);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  },
);
